import React from 'react';

const AppointmentTypeSelector = ({ 
  serviceLocationTypes, 
  selectedType, 
  onTypeChange, 
  primaryColor = 'blue-600',
  themeMode = 'light',
  businessName 
}) => {
  const { hasFixed, hasMobile } = serviceLocationTypes;
  
  // If only one option is available, auto-select it
  React.useEffect(() => {
    if (hasFixed && !hasMobile && !selectedType) {
      onTypeChange('fixed');
    } else if (hasMobile && !hasFixed && !selectedType) {
      onTypeChange('mobile');
    }
  }, [hasFixed, hasMobile, selectedType, onTypeChange]);

  // Don't show anything if no valid options
  if (!hasFixed && !hasMobile) {
    return null;
  }

  // Check if we only have one option available
  const hasOnlyOneOption = (hasFixed && !hasMobile) || (hasMobile && !hasFixed);

  const getRadioStyles = (isSelected) => {
    if (isSelected) return 'radio-themed-selected';
    return 'border-themed';
  };

  const options = [];
  
  if (hasMobile) {
    options.push({
      id: 'mobile',
      title: '🏠 We come to you',
      description: 'You\'ll provide your address in the next step'
    });
  }
  
  if (hasFixed) {
    options.push({
      id: 'fixed',
      title: `📍 Visit ${businessName || 'our location'}`,
      description: `You'll go to ${businessName || 'the business'} for your appointment`
    });
  }

  // If only one option, show informational display instead of radio buttons
  if (hasOnlyOneOption) {
    const singleOption = options[0];
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-themed-primary mb-2">
            Service Type
          </h3>
          <p className="text-sm text-themed-secondary">
            For this appointment:
          </p>
        </div>
        
        <div className="rounded-lg border border-themed bg-themed-card-elevated p-4">
          <div className="flex items-center">
            <span className="text-2xl mr-3">
              {singleOption.id === 'mobile' ? '🏠' : '📍'}
            </span>
            <div className="flex flex-col">
              <span className="block text-sm font-medium text-themed-primary">
                {singleOption.title}
              </span>
              <span className="block text-sm text-themed-secondary">
                {singleOption.description}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Multiple options - show radio button interface
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-themed-primary mb-2">
          How would you like to be served?
        </h3>
        <p className="text-sm text-themed-secondary">
          Select a service type to continue
        </p>
      </div>
      
      <div className="space-y-3">
        {options.map((option) => (
          <label
            key={option.id}
            className="relative flex cursor-pointer rounded-lg border border-themed bg-themed-card p-4 shadow-sm focus:outline-none hover-themed-tint transition-colors duration-200"
          >
            <input
              type="radio"
              name="appointmentType"
              value={option.id}
              checked={selectedType === option.id}
              onChange={(e) => onTypeChange(e.target.value)}
              className="sr-only"
            />
            <span
              className={`flex size-4 items-center justify-center rounded-full border mt-0.5 ${
                getRadioStyles(selectedType === option.id)
              }`}
            >
              {selectedType === option.id && (
                <span className="h-2 w-2 rounded-full bg-white" />
              )}
            </span>
            <span className="ml-3 flex flex-col">
              <span className="block text-sm font-medium text-themed-primary">
                {option.title}
              </span>
              <span className="block text-sm text-themed-secondary">
                {option.description}
              </span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default AppointmentTypeSelector;